import React from 'react';
import { useSectionControl } from '../contexts/SectionControlContext';
import { useIsAdmin } from '../hooks/useIsAdmin';
import { useContent } from '../contexts/ContentContext';
import InlineTranslationEdit from './InlineTranslationEdit';
import RichContentRenderer from './RichContentRenderer';
import {
  getContentBlockBackground,
  getContentBlockBgType,
  getContentBlockTextColor,
  getContentBlockTextSize,
  getContentBlockHeight,
  CONTENT_BLOCK_TEXT_SIZES,
  CONTENT_BLOCK_HEIGHT_VALUES,
  DEFAULT_CONTENT_BLOCK_HEIGHT,
} from '../utils/sectionWidth';

/**
 * Content Block section — colored/gradient banner with tag-editable text.
 * Admin mode: shows the block with InlineTranslationEdit (opens the standard tag editor).
 * Player mode: renders the styled block with translated text (supports HTML) or nothing if empty.
 *
 * Uses ContentContext's c() instead of TranslationContext's t() because c() checks
 * pendingContentChanges for immediate display after editing, before "Save All" is clicked.
 */
export default function ContentBlockSection({ sectionType }) {
  const sectionControl = useSectionControl();
  const { isAdmin } = useIsAdmin();
  const { c, resolveContentValue } = useContent();
  const section = sectionControl?.getSectionByType?.(sectionType);

  const background = getContentBlockBackground(section);
  const bgType = getContentBlockBgType(section);
  const textColor = getContentBlockTextColor(section);
  const textSize = getContentBlockTextSize(section);
  const blockHeight = getContentBlockHeight(section);

  const textSizeClass = CONTENT_BLOCK_TEXT_SIZES[textSize]?.className || 'text-base';
  const minHeight = CONTENT_BLOCK_HEIGHT_VALUES[blockHeight] || CONTENT_BLOCK_HEIGHT_VALUES[DEFAULT_CONTENT_BLOCK_HEIGHT];

  // Background style: solid color uses backgroundColor, gradient uses background
  const bgStyle = bgType === 'gradient'
    ? { background }
    : { backgroundColor: background };

  // Translation key for this content block's text
  // c() checks pendingContentChanges first, so edits show immediately
  const translationKey = `section.${sectionType}.text`;
  const contentValue = c(translationKey);
  const renderedContentValue = resolveContentValue(contentValue, { currentKey: translationKey, editMode: isAdmin });
  const hasContent = contentValue && contentValue.trim().length > 0;

  if (isAdmin) {
    const blockLabel = sectionType?.replace(/_/g, ' ').replace(/\b\w/g, ch => ch.toUpperCase()) || 'Content Block';

    return (
      <div
        className="w-full rounded-lg px-6 py-8 relative border-2 border-dashed border-white/30 flex items-center justify-center text-center"
        style={{ ...bgStyle, color: textColor, minHeight }}
      >
        {/* Faint label when empty */}
        {!hasContent && (
          <span className="text-white/40 text-xs font-medium uppercase tracking-wider select-none pointer-events-none absolute top-2 left-4">
            {blockLabel}
          </span>
        )}
        <InlineTranslationEdit
          translationKey={translationKey}
          value={contentValue}
          fallback="Click to edit text"
          editMode={true}
          multiline
          className={`${textSizeClass} font-medium leading-relaxed`}
        />
      </div>
    );
  }

  // Player mode: skip rendering entirely if no content is set
  if (!hasContent) return null;

  // Support HTML content from the tag editor
  const containsHtml = /<[^>]+>/.test(renderedContentValue);
  return (
    <div
      className="w-full rounded-lg px-6 py-8 flex items-center justify-center text-center"
      style={{ ...bgStyle, color: textColor, minHeight }}
    >
      {containsHtml ? (
        <RichContentRenderer
          html={renderedContentValue}
          variant="banner"
          centered
          className={`${textSizeClass} font-medium leading-relaxed w-full`}
        />
      ) : (
        <p className={`${textSizeClass} font-medium leading-relaxed m-0 whitespace-pre-wrap`}>
          {renderedContentValue}
        </p>
      )}
    </div>
  );
}
